import type { Lift, OpsPayload, Run } from './contract'

import { MOUNTAIN_ZONE, PERMANENT_UTC6_WALL_FROM, mountainFormatter } from './mountain-clock'
export { MOUNTAIN_ZONE }
const calendar = mountainFormatter('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' })
export const mountainDate = (date: Date) => calendar.format(date)
export function yesterday(date: Date): string {
  const day = new Date(`${mountainDate(date)}T12:00:00Z`)
  day.setUTCDate(day.getUTCDate() - 1)
  return day.toISOString().slice(0, 10)
}

const wallClock = new Intl.DateTimeFormat('sv-SE', {
  timeZone: MOUNTAIN_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})
const HOUR_MS = 3600_000
const SOURCE_SKEW_MS = 15 * 60_000

/**
 * ISO timestamps carry an explicit instant (or a 24-hour local clock). Older
 * space-separated 01–12 clocks omit AM/PM, so retain their check-time inference.
 */
export function sourceTime(value: string | undefined, checkedAt: Date): string | undefined {
  const parts =
    /^(\d{4}-\d{2}-\d{2})([T ])(\d{2}):(\d{2}):(\d{2})(\.\d{1,3})?(Z|[+-]\d{2}:?\d{2})?$/.exec(
      value ?? '',
    )
  const checked = checkedAt.getTime()
  if (!parts || !Number.isFinite(checked)) return undefined
  const [, day, separator, hh, mm, ss, fraction = '', zone] = parts
  const hour = Number(hh)
  if (hour > 23 || Number(mm) > 59 || Number(ss) > 59) return undefined
  const local = `${day}T${hh}:${mm}:${ss}`
  const target = Date.parse(local + fraction + 'Z')
  if (!Number.isFinite(target) || new Date(target).toISOString().slice(0, 19) !== local)
    return undefined

  if (zone) {
    if (separator !== 'T') return undefined
    if (zone !== 'Z' && (Number(zone.slice(1, 3)) > 23 || Number(zone.slice(-2)) > 59))
      return undefined
    const instant = Date.parse(local + fraction + zone)
    return Number.isFinite(instant) && instant <= checked + SOURCE_SKEW_MS
      ? new Date(instant).toISOString()
      : undefined
  }

  const legacy = separator === ' ' && hour >= 1 && hour <= 12
  const candidates = legacy
    ? [target + ((hour % 12) - hour) * HOUR_MS, target + ((hour % 12) + 12 - hour) * HOUR_MS]
    : [target]
  // Discover historical offsets on both sides of a transition. After Nov 1,
  // never trust old device/runtime tzdata that would still fall back to UTC-7.
  const offsets = new Set(
    [-24, 0, 24].map((hours) => {
      const instant = Math.floor(target / 1000) * 1000 + hours * HOUR_MS
      return Date.parse(wallClock.format(new Date(instant)).replace(' ', 'T') + 'Z') - instant
    }),
  )
  let latest: number | undefined
  for (const naive of candidates) {
    const possible =
      naive >= PERMANENT_UTC6_WALL_FROM
        ? [naive + 6 * HOUR_MS]
        : [...offsets]
            .map((offset) => naive - offset)
            .filter(
              (instant) =>
                wallClock.format(new Date(instant)).replace(' ', 'T') ===
                new Date(naive).toISOString().slice(0, 19),
            )
    for (const instant of possible)
      if (instant <= checked + SOURCE_SKEW_MS && (latest === undefined || instant > latest))
        latest = instant
  }
  return latest === undefined ? undefined : new Date(latest).toISOString()
}

export type ItemKind = 'lift' | 'run'
export interface ItemState {
  open: boolean
  groomed?: boolean
  status?: string
}
export interface Change {
  key: string
  kind: ItemKind
  id: number
  name: string
  area: string
  before: ItemState
  after: ItemState
}
export interface Briefing {
  version: 1
  timezone: typeof MOUNTAIN_ZONE
  current: { checkedAt: string; publishedAt?: string; stale: boolean; resort: OpsPayload['resort'] }
  baseline: { available: boolean; date: string; checkedAt?: string; publishedAt?: string }
  summary: {
    liftsOpened: number
    liftsStopped: number
    runsOpened: number
    runsClosed: number
    newlyGroomed: number
  }
  changes: Change[]
}
export const itemKey = (kind: ItemKind, item: { id: number }) => `${kind}:${item.id}`
const itemState = (item: Lift | Run): ItemState => ({
  open: item.open,
  ...('groomed' in item && { groomed: item.groomed }),
  ...(item.status && { status: item.status }),
})
export function items(payload: OpsPayload) {
  return payload.areas.flatMap((area) => [
    ...area.lifts.map((item) => ({
      key: itemKey('lift', item),
      kind: 'lift' as const,
      item,
      area: area.name,
    })),
    ...area.runs.map((item) => ({
      key: itemKey('run', item),
      kind: 'run' as const,
      item,
      area: area.name,
    })),
  ])
}
export function changesSince(before: OpsPayload, after: OpsPayload): Change[] {
  const old = new Map(items(before).map((row) => [row.key, row]))
  return items(after).flatMap((row) => {
    const prev = old.get(row.key)
    if (!prev) return [] // New inventory is not a newly opened run.
    const a = itemState(prev.item),
      b = itemState(row.item)
    if (a.open === b.open && a.groomed === b.groomed && a.status === b.status) return []
    return [
      {
        key: row.key,
        kind: row.kind,
        id: row.item.id,
        name: row.item.name,
        area: row.area,
        before: a,
        after: b,
      },
    ]
  })
}
export function makeBriefing(
  current: OpsPayload,
  previous: OpsPayload | null,
  now: Date,
  stale = false,
): Briefing {
  const changes = previous ? changesSince(previous, current) : []
  const count = (kind: ItemKind, open: boolean) =>
    changes.filter(
      (x) => x.kind === kind && x.before.open !== x.after.open && x.after.open === open,
    ).length
  return {
    version: 1,
    timezone: MOUNTAIN_ZONE,
    current: {
      resort: current.resort,
      checkedAt: current.checkedAt,
      ...(current.publishedAt && { publishedAt: current.publishedAt }),
      stale,
    },
    baseline: {
      available: !!previous,
      date: yesterday(now),
      ...(previous && {
        checkedAt: previous.checkedAt,
        ...(previous.publishedAt && { publishedAt: previous.publishedAt }),
      }),
    },
    summary: {
      liftsOpened: count('lift', true),
      liftsStopped: count('lift', false),
      runsOpened: count('run', true),
      runsClosed: count('run', false),
      newlyGroomed: changes.filter((x) => x.kind === 'run' && !x.before.groomed && x.after.groomed)
        .length,
    },
    changes,
  }
}

const stamp = (value: unknown): value is string =>
  typeof value === 'string' && Number.isFinite(Date.parse(value))
const itemStateValid = (value: ItemState) =>
  !!value &&
  typeof value.open === 'boolean' &&
  (value.groomed === undefined || typeof value.groomed === 'boolean') &&
  (value.status === undefined || typeof value.status === 'string')
export function validBriefing(value: unknown): value is Briefing {
  if (!value || typeof value !== 'object') return false
  const v = value as Briefing
  return (
    v.version === 1 &&
    v.timezone === MOUNTAIN_ZONE &&
    !!v.current &&
    stamp(v.current.checkedAt) &&
    (v.current.publishedAt === undefined || stamp(v.current.publishedAt)) &&
    typeof v.current.stale === 'boolean' &&
    !!v.current.resort &&
    ['open', 'closed'].includes(v.current.resort.status) &&
    !!v.baseline &&
    typeof v.baseline.available === 'boolean' &&
    /^\d{4}-\d{2}-\d{2}$/.test(v.baseline.date) &&
    (!v.baseline.available || stamp(v.baseline.checkedAt)) &&
    (v.baseline.publishedAt === undefined || stamp(v.baseline.publishedAt)) &&
    !!v.summary &&
    ['liftsOpened', 'liftsStopped', 'runsOpened', 'runsClosed', 'newlyGroomed'].every((key) => {
      const n = v.summary[key as keyof Briefing['summary']]
      return Number.isInteger(n) && n >= 0
    }) &&
    Array.isArray(v.changes) &&
    (!v.baseline.available ? v.changes.length === 0 : true) &&
    v.changes.every(
      (x) =>
        !!x &&
        (x.kind === 'lift' || x.kind === 'run') &&
        x.key === itemKey(x.kind, x) &&
        Number.isFinite(x.id) &&
        typeof x.name === 'string' &&
        typeof x.area === 'string' &&
        itemStateValid(x.before) &&
        itemStateValid(x.after),
    )
  )
}
